import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import type { RefObject } from 'react';

import { DURATION, EASE, STAGGER } from '@animations/index';
import { usePrefersReducedMotion } from '@stores/index';

import { useGsapContext } from './useGsapContext';

/** How far into the viewport an element's top must be before it reveals. */
const REVEAL_START = 'top 90%';

/**
 * Reveals every `[data-reveal]` element inside `scope` as it scrolls into
 * view, in reading order.
 *
 * The animation communicates sequence: elements arrive in the order they
 * should be read, so a staggered group reads as a group. It runs once per
 * element and never replays on scroll back, because re-animating content
 * the visitor has already read is noise.
 *
 * Animates the `--reveal` custom property rather than opacity directly; see
 * base/_motion.scss for why glass surfaces cannot fade their own opacity.
 *
 * Skipped entirely under reduced motion. The CSS fallback of 1 leaves
 * every element fully visible, so nothing depends on this having run.
 */
export const useReveal = (scope: RefObject<HTMLElement | null>): void => {
  const prefersReducedMotion = usePrefersReducedMotion();

  useGsapContext(
    () => {
      if (prefersReducedMotion) return;

      const targets = gsap.utils.toArray<HTMLElement>('[data-reveal]');
      if (targets.length === 0) return;

      gsap.set(targets, { '--reveal': 0 });

      ScrollTrigger.batch(targets, {
        start: REVEAL_START,
        once: true,
        onEnter: (batch) =>
          gsap.to(batch, {
            '--reveal': 1,
            duration: DURATION.slow,
            ease: EASE.out,
            stagger: STAGGER.base,
            overwrite: true,
          }),
      });
    },
    scope,
    [prefersReducedMotion],
  );
};
